import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { UrlMetadata } from '@/hooks/queries/useUrlMetadataQuery';

export interface RichLinkPreviewProps {
  metadata: UrlMetadata;
  layout?: 'responsive' | 'vertical';
  onArticleClick?: () => void;
}

export function RichLinkPreview({
  metadata,
  layout = 'responsive',
  onArticleClick,
}: RichLinkPreviewProps) {
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const isVertical = layout === 'vertical';
  const imageUrl = metadata.image_url;
  const shouldRenderImage = Boolean(imageUrl && imageUrl !== failedImageUrl);
  let hostname: string;
  try {
    hostname = new URL(metadata.url).hostname.replace('www.', '');
  } catch {
    // Invalid URL fallback
    hostname = metadata.url;
  }

  const displayTitle = metadata.title || metadata.site_name || hostname;
  const displaySite = metadata.site_name || hostname;

  return (
    <a
      href={metadata.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onArticleClick}
      className="group block w-full overflow-hidden rounded-lg bg-raised shadow-none ring-1 ring-outline-default/30 transition-all duration-fast ease-smooth hover:bg-subtle hover:ring-outline-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:translate-y-px"
      style={{ outlineColor: 'var(--focus-ring-color)' }}
    >
      <div className={`flex h-full flex-col ${isVertical ? '' : 'sm:flex-row'}`}>
        {shouldRenderImage && imageUrl && (
          <div
            className={`relative shrink-0 overflow-hidden bg-subtle ${
              isVertical
                ? 'h-32 w-full rounded-t-lg'
                : 'h-32 w-full rounded-t-lg sm:h-auto sm:min-h-36 sm:w-56 sm:self-stretch sm:rounded-l-lg sm:rounded-tr-none'
            }`}
          >
            <img
              src={imageUrl}
              alt={displayTitle}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-medium group-hover:scale-105"
              onError={() => setFailedImageUrl(imageUrl)}
            />
          </div>
        )}
        <div className="flex flex-1 flex-col justify-center p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-small font-bold tracking-wider text-glyph-secondary uppercase">
              {displaySite}
            </span>
          </div>
          <h4 className="line-clamp-2 text-body font-bold text-glyph-primary transition-colors">
            {displayTitle}
          </h4>
          {metadata.description && (
            <p className="mt-1 line-clamp-2 text-small text-glyph-secondary">
              {metadata.description}
            </p>
          )}
          <div className="mt-3 flex items-center pt-2 text-small font-bold text-accent-primary transition-transform duration-fast group-hover:translate-x-1">
            <span>Read Article</span>
            <ArrowUpRight className="ml-1 h-4 w-4" />
          </div>
        </div>
      </div>
    </a>
  );
}
